import { useState, useRef } from 'react';
import { Upload, CheckCircle, XCircle, AlertTriangle, Download, FileText } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { api } from '../../utils/api';

// Parse "Class 1 - A" → { name: "Class 1", section: "A" }
function parseClassLabel(label) {
  const parts = label.split(' - ');
  return { name: parts[0]?.trim() || label, section: parts[1]?.trim() || 'A' };
}

// ── CSV headers (exact order) ─────────────────────────────────
const HEADERS = [
  'Admission No', 'Roll No', 'Student Name', 'Gender', 'DOB',
  'Class', 'Section', 'Category', 'Caste', 'Religion',
  'Aadhar Number', 'Permanent Education Number (PEN)',
  'Father Name', 'Father Phone', 'Mother Name', 'Mother Phone',
  'Guardian Is', 'Guardian Name', 'Guardian Relation', 'Guardian Phone',
  'Guardian Occupation', 'Guardian Email', 'Guardian Address',
  'Student Mobile', 'Admission Date', 'Status', 'Previous Session Balance',
];

// ── Download blank template ───────────────────────────────────
function downloadTemplate() {
  const bom = '﻿';
  const header = HEADERS.join(',');
  const rows = Array(5).fill(HEADERS.map(() => '').join(',')).join('\n');
  const blob = new Blob([bom + header + '\n' + rows], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'student_import_template.csv'; a.click();
  URL.revokeObjectURL(url);
}

// ── CSV line parser (handles quoted fields) ───────────────────
function parseCsvLine(line) {
  const result = []; let cur = ''; let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"' && !inQ) { inQ = true; continue; }
    if (ch === '"' && inQ && line[i+1] === '"') { cur += '"'; i++; continue; }
    if (ch === '"' && inQ) { inQ = false; continue; }
    if (ch === ',' && !inQ) { result.push(cur.trim()); cur = ''; continue; }
    cur += ch;
  }
  result.push(cur.trim());
  return result;
}

// ── Date normalizer: DD-MM-YYYY or YYYY-MM-DD → YYYY-MM-DD ───
function fmtDate(raw) {
  if (!raw?.trim()) return null;
  const d = raw.trim();
  // DD-MM-YYYY or DD/MM/YYYY
  const dmy = d.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2,'0')}-${dmy[1].padStart(2,'0')}`;
  // YYYY-MM-DD already
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  return null;
}

// ── Class label for DB matching ───────────────────────────────
function makeClassLabel(cls, sec) {
  const c = String(cls).trim();
  const s = String(sec || 'A').trim().toUpperCase();
  const upper = c.toUpperCase();
  const name = upper === 'LKG' ? 'LKG'
    : upper === 'UKG' ? 'UKG'
    : upper.includes('NURSERY') ? 'Nursery'
    : `Class ${c}`;
  return `${name} - ${s}`;
}

// ── Parse CSV rows into import records ────────────────────────
function mapRows(dataRows) {
  const students = [];
  let seq = Date.now();

  for (const cols of dataRows) {
    if (!cols || cols.every(c => !c?.trim())) continue;

    const admNo       = cols[0]?.trim()  || '';
    const rollNo      = cols[1]?.trim()  || '';
    const fullName    = cols[2]?.trim()  || '';
    if (!fullName) continue;

    const genderRaw   = cols[3]?.trim().toLowerCase() || '';
    const dob         = cols[4]?.trim()  || '';
    const classRaw    = cols[5]?.trim()  || '';
    const section     = cols[6]?.trim()  || 'A';
    const category    = cols[7]?.trim()  || '';
    const caste       = cols[8]?.trim()  || '';
    const religion    = cols[9]?.trim()  || '';
    const aadhar      = cols[10]?.trim() || '';
    const pen         = cols[11]?.trim() || '';
    const fatherName  = cols[12]?.trim() || '';
    const fatherPhone = cols[13]?.trim() || '';
    const motherName  = cols[14]?.trim() || '';
    const motherPhone = cols[15]?.trim() || '';
    const guardianIs  = cols[16]?.trim().toLowerCase() || 'father';
    const guardianName= cols[17]?.trim() || '';
    const guardianRel = cols[18]?.trim().toLowerCase() || '';
    const guardianPhone=cols[19]?.trim() || '';
    const guardianOcc = cols[20]?.trim() || '';
    const guardianEmail=cols[21]?.trim() || '';
    const guardianAddr= cols[22]?.trim() || '';
    const stuPhone    = cols[23]?.trim() || '';
    const admDate     = cols[24]?.trim() || '';
    const statusRaw   = cols[25]?.trim().toLowerCase() || 'active';
    const prevBal     = parseFloat(cols[26]?.trim() || '0') || 0;

    const nameParts = fullName.replace(/\s+/g, ' ').split(' ');
    const firstName = nameParts[0];
    const lastName  = nameParts.slice(1).join(' ');
    const gender    = ['female','f','girl'].includes(genderRaw) ? 'girl' : 'boy';
    const status    = ['active','inactive'].includes(statusRaw) ? statusRaw : 'active';
    const classLabel= makeClassLabel(classRaw, section);

    // Parent: prefer explicit guardian, then by guardian-is flag
    let parentName, parentPhone, parentRelation, parentOcc;
    if (guardianName) {
      parentName     = guardianName;
      parentPhone    = guardianPhone || fatherPhone || motherPhone;
      parentRelation = guardianRel || guardianIs || 'father';
      parentOcc      = guardianOcc;
    } else if (guardianIs === 'mother') {
      parentName     = motherName || fatherName;
      parentPhone    = motherPhone || fatherPhone;
      parentRelation = 'mother';
      parentOcc      = guardianOcc;
    } else {
      parentName     = fatherName || motherName;
      parentPhone    = fatherPhone || motherPhone;
      parentRelation = 'father';
      parentOcc      = guardianOcc;
    }

    const stuId  = admNo ? `STU-${admNo}` : `VS-${seq++}`;
    const parId  = `VP-${seq++}`;
    const admNum = admNo || `ADM${Date.now()}${seq}`;

    students.push({
      id: stuId,
      admissionNumber: admNum,
      rollNumber: rollNo || '',
      firstName, lastName, gender,
      dob:           fmtDate(dob),
      classLabel,
      category, caste, religion,
      aadharNumber:  aadhar,
      penNumber:     pen,
      fatherName, fatherPhone,
      motherName, motherPhone,
      address:       guardianAddr,
      phone:         stuPhone,
      admissionDate: fmtDate(admDate),
      status,
      previousYearBalance: prevBal,
      parentId:       parId,
      parentName:     parentName || '',
      parentRelation: parentRelation || 'father',
      parentPhone:    parentPhone || '',
      parentOccupation: parentOcc || '',
      parentEmail:    guardianEmail || '',
    });
  }
  return students;
}

// ─────────────────────────────────────────────────────────────
export default function StudentImport() {
  const { classes, reloadData } = useApp();
  const [rows, setRows]     = useState([]);
  const [step, setStep]     = useState('upload');
  const [result, setResult] = useState(null);
  const [error, setError]   = useState('');
  const [progress, setProgress] = useState(0);
  const fileRef = useRef();

  const handleFile = (file) => {
    if (!file) return;
    setError('');
    const reader = new FileReader();
    reader.onload = (e) => {
      // Strip BOM if present
      let text = e.target.result.replace(/^﻿/, '');
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      if (lines.length < 2) { setError('File is empty or has no data rows.'); return; }

      // Detect header row by checking if first cell looks like "Admission No"
      const firstCells = parseCsvLine(lines[0]);
      const hasHeader = firstCells[0].toLowerCase().includes('admission') || firstCells[2]?.toLowerCase().includes('student');
      const dataLines = hasHeader ? lines.slice(1) : lines;

      const parsed = dataLines.map(parseCsvLine);
      const mapped = mapRows(parsed);
      if (mapped.length === 0) { setError('No valid student rows found. Make sure Student Name column is filled.'); return; }
      setRows(mapped);
      setStep('preview');
    };
    reader.readAsText(file, 'utf-8');
  };

  function resolveClassIds(rows) {
    return rows.map(row => {
      const match = classes.find(c => `${c.name} - ${c.section}` === row.classLabel);
      return { ...row, classId: match?.id || null, sectionId: row.classLabel.split(' - ')[1] || 'A' };
    });
  }

  const handleImport = async () => {
    setStep('importing'); setProgress(0);

    // 1. Auto-create any missing classes before importing students
    let currentClasses = [...classes];
    for (const lbl of unmappedClasses) {
      const { name, section } = parseClassLabel(lbl);
      try {
        const newClass = {
          id:       `CLS-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
          name,
          section,
          capacity: 40,
        };
        await api.create('classes', newClass);
        currentClasses.push(newClass);
      } catch (e) {
        // ignore duplicate, will resolve below
      }
    }

    // 2. Resolve class IDs using the updated class list
    const resolved = rows.map(row => {
      const match = currentClasses.find(c => `${c.name} - ${c.section}` === row.classLabel);
      return { ...row, classId: match?.id || null, sectionId: row.classLabel.split(' - ')[1] || 'A' };
    });

    // 3. Batch import students
    const BATCH = 50;
    let imported = 0; let allErrors = [];
    for (let i = 0; i < resolved.length; i += BATCH) {
      try {
        const res = await api.importStudents(resolved.slice(i, i + BATCH));
        imported += res.imported;
        allErrors = allErrors.concat(res.errors || []);
      } catch (e) {
        allErrors.push({ row: `batch ${Math.floor(i/BATCH)+1}`, error: e.message });
      }
      setProgress(Math.min(100, Math.round(((i + BATCH) / resolved.length) * 100)));
    }
    await reloadData();
    setResult({ imported, errors: allErrors, total: resolved.length });
    setStep('done');
  };

  const importClasses   = [...new Set(rows.map(r => r.classLabel))].sort();
  const unmappedClasses = importClasses.filter(lbl => !classes.find(c => `${c.name} - ${c.section}` === lbl));

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-800">Import Students</h1>
        <p className="text-sm text-gray-500 mt-1">Upload the CSV template filled with student data to bulk-import into the database.</p>
      </div>

      {/* ── Upload ── */}
      {step === 'upload' && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 space-y-6">
          <div
            className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors"
            onClick={() => fileRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
          >
            <Upload size={40} className="mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600 font-medium">Click to upload or drag &amp; drop</p>
            <p className="text-sm text-gray-400 mt-1">CSV file · UTF-8 encoding</p>
            <input ref={fileRef} type="file" accept=".csv" className="hidden"
              onChange={e => handleFile(e.target.files[0])} />
          </div>

          {error && <p className="text-red-600 text-sm flex items-center gap-1"><XCircle size={14}/>{error}</p>}

          {/* Column guide */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <FileText size={15} className="text-purple-500" /> CSV Column Format
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
              {HEADERS.map((h, i) => (
                <div key={h} className="flex items-start gap-1.5 text-xs">
                  <span className="text-gray-400 font-mono w-5 flex-shrink-0">{i+1}.</span>
                  <span className="text-gray-600">{h}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500 space-y-1">
              <p><strong>DOB / Admission Date:</strong> DD-MM-YYYY or YYYY-MM-DD</p>
              <p><strong>Gender:</strong> Male / Female</p>
              <p><strong>Guardian Is:</strong> Father / Mother</p>
              <p><strong>Status:</strong> active / inactive (default: active)</p>
              <p><strong>Previous Session Balance:</strong> number (₹)</p>
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={downloadTemplate}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">
              <Download size={14}/> Download Template
            </button>
          </div>
        </div>
      )}

      {/* ── Preview ── */}
      {step === 'preview' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between">
            <div>
              <p className="font-semibold text-gray-800">{rows.length} students ready to import</p>
              <p className="text-sm text-gray-500">{importClasses.length} class{importClasses.length !== 1 ? 'es' : ''} · {rows.filter(r => r.previousYearBalance > 0).length} with previous balance</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setRows([]); setStep('upload'); }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleImport}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700">
                Import Now
              </button>
            </div>
          </div>

          {unmappedClasses.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="font-semibold text-blue-800 flex items-center gap-2">
                <CheckCircle size={16}/> {unmappedClasses.length} new class(es) will be created automatically
              </p>
              <p className="text-sm text-blue-700 mt-1">
                These classes don't exist yet and will be created before students are imported.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {unmappedClasses.map(c => (
                  <span key={c} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">{c}</span>
                ))}
              </div>
            </div>
          )}

          {/* Class breakdown */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 font-semibold text-sm text-gray-700">Class Breakdown</div>
            <div className="divide-y divide-gray-50">
              {importClasses.map(lbl => {
                const count  = rows.filter(r => r.classLabel === lbl).length;
                const mapped = classes.find(c => `${c.name} - ${c.section}` === lbl);
                return (
                  <div key={lbl} className="flex items-center justify-between px-4 py-2 text-sm">
                    <span className="font-medium">{lbl}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500">{count} students</span>
                      {mapped
                        ? <span className="text-green-600 text-xs flex items-center gap-1"><CheckCircle size={12}/>Mapped</span>
                        : <span className="text-yellow-600 text-xs flex items-center gap-1"><AlertTriangle size={12}/>Not in DB</span>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Preview table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 font-semibold text-sm text-gray-700">
              Preview (first 10 rows)
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['#','Adm No','Student Name','Class','Gender','DOB','Father','Mother','Guardian Phone','Prev. Balance','Status'].map(h => (
                      <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.slice(0, 10).map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-400 text-xs">{i+1}</td>
                      <td className="px-3 py-2 text-xs font-mono text-gray-500">{r.admissionNumber}</td>
                      <td className="px-3 py-2 font-medium">{r.firstName} {r.lastName}</td>
                      <td className="px-3 py-2 text-gray-600">{r.classLabel}</td>
                      <td className="px-3 py-2 capitalize text-gray-600">{r.gender}</td>
                      <td className="px-3 py-2 text-gray-600">{r.dob || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{r.fatherName || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{r.motherName || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{r.parentPhone || '—'}</td>
                      <td className="px-3 py-2 text-right">
                        {r.previousYearBalance > 0
                          ? <span className="text-orange-600 font-semibold">₹{r.previousYearBalance.toLocaleString('en-IN')}</span>
                          : <span className="text-gray-300">—</span>
                        }
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${r.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > 10 && (
                <p className="px-4 py-2 text-xs text-gray-400">… and {rows.length - 10} more rows</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Importing ── */}
      {step === 'importing' && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-4"/>
          <p className="font-semibold text-gray-700">Importing students…</p>
          <p className="text-sm text-gray-500 mt-1">{progress}% complete</p>
          <div className="mt-4 bg-gray-100 rounded-full h-2 max-w-xs mx-auto">
            <div className="bg-purple-500 h-2 rounded-full transition-all" style={{ width: `${progress}%` }}/>
          </div>
        </div>
      )}

      {/* ── Done ── */}
      {step === 'done' && result && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-6 ${result.errors.length === 0 ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
            <div className="flex items-center gap-3 mb-3">
              {result.errors.length === 0
                ? <CheckCircle size={24} className="text-green-600"/>
                : <AlertTriangle size={24} className="text-yellow-600"/>
              }
              <div>
                <p className="font-bold text-gray-800">Import Complete</p>
                <p className="text-sm text-gray-600">
                  {result.imported} of {result.total} students imported successfully.
                  {result.errors.length > 0 && ` ${result.errors.length} failed.`}
                </p>
              </div>
            </div>
            <button onClick={() => { setRows([]); setStep('upload'); setResult(null); }}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700">
              Import Another File
            </button>
          </div>

          {result.errors.length > 0 && (
            <div className="bg-white rounded-xl border border-red-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-red-100 font-semibold text-sm text-red-700">
                Failed Rows ({result.errors.length})
              </div>
              <div className="divide-y divide-red-50 max-h-48 overflow-y-auto">
                {result.errors.map((e, i) => (
                  <div key={i} className="px-4 py-2 text-sm flex gap-2">
                    <span className="font-medium text-gray-700">{e.row}</span>
                    <span className="text-red-600">{e.error}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
